import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

import { MAIN_TABS, orderAndFilterMemberTabs, SUB_TABS } from './memberNavigation';

/**
 * Desktop sticky navigation with main tabs and sub tabs
 * Both rows stick below the header when scrolling
 */
const DesktopNav = ({
  activeTab,
  onTabChange,
  subSection,
  onSubSectionChange,
  canViewBilling = true,
  contentRestricted = false,
  isSectionVisible = () => true,
  isLoaded = true,
  navigationPreferences = {},
}) => {
  const hasSubTabs = SUB_TABS[activeTab];
  const allSubTabs = hasSubTabs ? SUB_TABS[activeTab] : [];

  // Filter tabs based on permissions, content access, and portal section visibility
  const accessibleTabs = MAIN_TABS.filter((tab) => {
    if (tab.id === 'billing') return canViewBilling;
    if (tab.id === 'resources' || tab.id === 'learning') return !contentRestricted;
    return true;
  }).filter((tab) => isSectionVisible(tab.id));
  const visibleTabs = orderAndFilterMemberTabs(accessibleTabs, navigationPreferences);

  // Filter sub-tabs by portal section visibility
  const currentSubTabs = allSubTabs.filter((sub) =>
    isSectionVisible(`${activeTab}.${sub.id}`)
  );

  // Sticky top positions - header is at top:0, so main tabs at 64px (header height)
  // Banner has higher z-index and sits on top of everything
  const mainTabsTop = '64px';   // right below header
  const subTabsTop = '112px';   // 64 + 48 = right below main tabs

  // Get current sub-section index for arrow navigation
  const currentSubIndex = currentSubTabs.findIndex((s) => s.id === subSection);

  const goToPrevSection = () => {
    if (currentSubIndex > 0) {
      onSubSectionChange(currentSubTabs[currentSubIndex - 1].id);
    }
  };

  const goToNextSection = () => {
    if (currentSubIndex < currentSubTabs.length - 1) {
      onSubSectionChange(currentSubTabs[currentSubIndex + 1].id);
    }
  };

  return (
    <>
      {/* Main Tabs Row - hidden on mobile */}
      <div
        className="hidden md:block z-[50] bg-[#FAF7F2] dark:bg-[#1A1814] border-b border-stone-200 dark:border-[#C9A227]/20 shadow-sm"
        style={{ top: mainTabsTop, height: '48px', position: 'sticky' }}
      >
        <div className="max-w-6xl mx-auto h-full px-4 flex items-center">
          <div
            className="w-full flex gap-1 rounded-lg bg-stone-100 dark:bg-[#2D2A26] p-1"
            data-testid="desktop-nav-main-tabs"
            role="tablist"
          >
            {!isLoaded
              ? MAIN_TABS.map((tab) => (
                  <div
                    key={tab.id}
                    className="flex-1 flex items-center justify-center gap-2 py-1.5 px-4"
                    data-testid="desktop-nav-skeleton"
                  >
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))
              : visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      data-testid="desktop-nav-tab"
                      data-tab-id={tab.id}
                      data-active={isActive ? 'true' : 'false'}
                      onClick={() => onTabChange(tab.id)}
                      className={`
                        flex-1 flex items-center justify-center gap-2 py-1.5 px-4
                        rounded-md text-sm font-medium transition-all
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B1B30]/50
                        ${isActive
                          ? 'bg-white dark:bg-[#1A1814] text-[#9B1B30] shadow-sm'
                          : 'text-stone-600 dark:text-stone-300 hover:bg-stone-200/70 dark:hover:bg-[#3D3A36] hover:text-stone-900 dark:hover:text-stone-100'
                        }
                      `}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
          </div>
        </div>
      </div>

      {/* Sub Tabs Row (conditional) - hidden on mobile */}
      {hasSubTabs && currentSubTabs.length > 0 && (
        <div
          className="hidden md:block z-[45] bg-[#FAF7F2]/95 dark:bg-[#1A1814]/95 backdrop-blur-sm border-b border-stone-200/50 dark:border-[#C9A227]/20"
          style={{ top: subTabsTop, position: 'sticky' }}
        >
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-2">
            {/* Previous Arrow */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={goToPrevSection}
              disabled={currentSubIndex === 0}
              className="h-8 w-8 p-0 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Section Pills */}
            <div className="flex-1 flex items-center justify-center gap-1 overflow-x-auto">
              {currentSubTabs.map((section) => {
                const Icon = section.icon;
                const isActive = subSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => onSubSectionChange(section.id)}
                    className={`
                      flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                      transition-all duration-200 whitespace-nowrap
                      ${isActive
                        ? 'bg-[#9B1B30] text-white shadow-md'
                        : 'bg-stone-100 dark:bg-[#2D2A26] text-stone-600 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-[#3D3A36]'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{section.desktopLabel || section.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Next Arrow */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={goToNextSection}
              disabled={currentSubIndex === currentSubTabs.length - 1}
              className="h-8 w-8 p-0 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default DesktopNav;
